import { useEffect, useState, type ReactNode } from 'react';

/**
 * Slider with a typeable value.
 *
 * The number is an input, not a label, because the slider step can't express
 * every value someone needs — 7.83 Hz snaps to 8 on a 0.5 step. Drag for a
 * rough value, type for an exact one.
 */
export function Slider({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  hint,
  disabled,
  typeable = true,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  onChange: (v: number) => void;
  hint?: ReactNode;
  disabled?: boolean;
  typeable?: boolean;
}) {
  // Local draft so a half-typed "7." isn't clobbered on every keystroke.
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = () => {
    const n = Number(draft);
    if (!Number.isFinite(n)) {
      setDraft(String(value));
      return;
    }
    const clamped = Math.min(max, Math.max(min, n));
    onChange(Number(clamped.toFixed(2)));
    setDraft(String(Number(clamped.toFixed(2))));
  };

  return (
    <div className="field">
      <div className="field-head">
        <span className="field-label">{label}</span>
        <span className="field-value">
          {typeable ? (
            <input
              type="text"
              inputMode="decimal"
              value={draft}
              disabled={disabled}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              aria-label={`${label} value`}
              style={{
                width: `${Math.max(3, draft.length + 1)}ch`,
                minHeight: 34,
                padding: '2px 6px',
                textAlign: 'right',
                font: 'inherit',
                display: 'inline-block',
              }}
            />
          ) : (
            value
          )}
          {unit ? <span className="faint"> {unit}</span> : null}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
      />
      {hint ? <div className="faint">{hint}</div> : null}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}


export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="card-head">
          <h2 className="card-title grow">{title}</h2>
          <button className="btn small ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function formatWhen(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
